// ============================================================================
// Folder Access Support
// ----------------------------------------------------------------------------
// The File System Access API is only reliable in real Chrome/Edge browser
// contexts. Embedded/browser-automation shells can expose partial APIs or block
// the picker at click time, so every folder-sync surface should share the same
// operator-facing explanation.
// ============================================================================

const REAL_BROWSER_SUFFIX = 'Open QuoteMaker in Google Chrome or Microsoft Edge, then choose the sync folder from there. The in-app browser is still useful for UI checks, but it cannot reliably grant Windows folder permissions.';

export function hasDirectoryPicker(win: (Window & typeof globalThis) | undefined = typeof window !== 'undefined' ? window : undefined): boolean {
    return typeof (win as any)?.showDirectoryPicker === 'function';
}

// PB-CP-1 (file-explorer-redesign 2026-05-11): mirror of hasDirectoryPicker
// for FileSystemObserver feature-detect. GA in Chrome 133+ (Feb 2025); not
// supported on Firefox/Safari/Android. Per Council seat #5 PB-CP-0, no
// origin-trial token required — the API is on the platform baseline now.
//
// Defensive: the constructor MAY exist but throw on some flag-gated builds;
// PB-CP-1's observer registration wraps `new FileSystemObserver(...)` in
// a try/catch, so this helper only guarantees the constructor is present.
export function hasFileSystemObserver(win: (Window & typeof globalThis) | undefined = typeof window !== 'undefined' ? window : undefined): boolean {
    return typeof (win as any)?.FileSystemObserver === 'function';
}

export function getFolderAccessUnsupportedMessage(feature = 'Folder sync'): string {
    return `${feature} needs real Chrome or Microsoft Edge with local folder picker support. ${REAL_BROWSER_SUFFIX}`;
}

export function isDirectoryPickerBlockedError(err: unknown): boolean {
    if (!err || (err as { name?: unknown })?.name === 'AbortError') return false;
    const name = String((err as { name?: unknown })?.name || '').toLowerCase();
    const message = String((err as { message?: unknown })?.message || err || '').toLowerCase();
    return name === 'notallowederror'
        || name === 'securityerror'
        || message.includes('showdirectorypicker')
        || message.includes('user activation')
        || message.includes('user gesture')
        || message.includes('handling a user gesture')
        || message.includes('file picker')
        || message.includes('directory picker')
        || message.includes('blocked')
        || message.includes('not allowed')
        || message.includes('permission');
}

export function getDirectoryPickerBlockedMessage(feature = 'Folder sync'): string {
    return `${feature} could not open the local folder picker in this browser context. ${REAL_BROWSER_SUFFIX}`;
}

export function normaliseDirectoryPickerError(
    err: unknown,
    feature = 'Folder sync',
    win: (Window & typeof globalThis) | undefined = typeof window !== 'undefined' ? window : undefined
): Error {
    if (!hasDirectoryPicker(win)) {
        return new Error(getFolderAccessUnsupportedMessage(feature));
    }
    if (isDirectoryPickerBlockedError(err)) {
        return new Error(getDirectoryPickerBlockedMessage(feature));
    }
    return err instanceof Error ? err : new Error(String(err || `${feature} failed`));
}
